import React, { useEffect, useRef } from 'react';
import { Dialog, Box, Typography } from '@mui/material';

interface HtmlPreviewModalProps {
  open: boolean;
  onClose: () => void;
  html: string;
  onFormSubmit?: (formData: Record<string, any>) => void;
}

const HtmlPreviewModal: React.FC<HtmlPreviewModalProps> = ({
  open,
  onClose,
  html,
  onFormSubmit
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // For security, you might want to check event.origin
      if (event.data && event.data.type === 'FORM_SUBMISSION' && onFormSubmit) {
        onFormSubmit(event.data.formData);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [onFormSubmit]);

  const handleIframeLoad = () => {
    const iframe = iframeRef.current;
    if (!iframe || !iframe.contentDocument) return;

    try {
      // Inject JavaScript to handle form submissions
      const script = iframe.contentDocument.createElement('script');
      script.textContent = `
        (function() {
          // Wait for DOM to be ready
          function ready(fn) {
            if (document.readyState !== 'loading') {
              fn();
            } else {
              document.addEventListener('DOMContentLoaded', fn);
            }
          }

          ready(function() {
            // Find all forms in the document
            const forms = document.querySelectorAll('form');
            
            forms.forEach(function(form) {
              form.addEventListener('submit', function(e) {
                e.preventDefault(); // Prevent default form submission
                
                // Collect form data
                const formData = new FormData(form);
                const data = {};
                
                // Convert FormData to plain object
                for (let [key, value] of formData.entries()) {
                  // Handle multiple values for the same key (like checkboxes)
                  if (data[key]) {
                    if (Array.isArray(data[key])) {
                      data[key].push(value);
                    } else {
                      data[key] = [data[key], value];
                    }
                  } else {
                    data[key] = value;
                  }
                }
                
                // Also collect unchecked checkboxes and radio buttons
                const uncheckedInputs = form.querySelectorAll('input[type="checkbox"]:not(:checked), input[type="radio"]:not(:checked)');
                uncheckedInputs.forEach(function(input) {
                  if (input.name && !data[input.name]) {
                    if (input.type === 'checkbox') {
                      data[input.name] = false;
                    }
                  }
                });
                
                // Send data to parent window
                window.parent.postMessage({
                  type: 'FORM_SUBMISSION',
                  formData: data
                }, '*');
                
                // Optionally show a success message
                const successDiv = document.createElement('div');
                successDiv.innerHTML = '<div style="background: #d4edda; color: #155724; padding: 10px; margin: 10px 0; border: 1px solid #c3e6cb; border-radius: 4px;">Form submitted successfully! Data has been added to workflow inputs.</div>';
                form.parentNode.insertBefore(successDiv, form.nextSibling);
                
                // Hide the form or disable submit button
                const submitButtons = form.querySelectorAll('button[type="submit"], input[type="submit"]');
                submitButtons.forEach(function(btn) {
                  btn.disabled = true;
                  btn.textContent = 'Submitted';
                });
              });
            });
            
            // If no forms found, look for buttons that might trigger form-like behavior
            if (forms.length === 0) {
              const buttons = document.querySelectorAll('button, input[type="button"]');
              buttons.forEach(function(button) {
                button.addEventListener('click', function(e) {
                  // Try to collect any input data on the page
                  const inputs = document.querySelectorAll('input, select, textarea');
                  const data = {};
                  
                  inputs.forEach(function(input) {
                    if (input.name || input.id) {
                      const key = input.name || input.id;
                      if (input.type === 'checkbox' || input.type === 'radio') {
                        data[key] = input.checked;
                      } else {
                        data[key] = input.value;
                      }
                    }
                  });
                  
                  // Only send if we found some data
                  if (Object.keys(data).length > 0) {
                    window.parent.postMessage({
                      type: 'FORM_SUBMISSION',
                      formData: data
                    }, '*');
                    
                    // Show success message
                    const successDiv = document.createElement('div');
                    successDiv.innerHTML = '<div style="background: #d4edda; color: #155724; padding: 10px; margin: 10px 0; border: 1px solid #c3e6cb; border-radius: 4px;">Data submitted successfully! Information has been added to workflow inputs.</div>';
                    document.body.appendChild(successDiv);
                    
                    button.disabled = true;
                    button.textContent = 'Submitted';
                  }
                });
              });
            }
          });
        })();
      `;
      
      iframe.contentDocument.head.appendChild(script);
    } catch (error) {
      console.error('Error injecting script into iframe:', error);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>HTML Preview</Typography>
        <Box sx={{ 
          maxHeight: '80vh', 
          overflow: 'auto',
          p: 2.5 
        }}>
          <iframe
            ref={iframeRef}
            srcDoc={html}
            onLoad={handleIframeLoad}
            style={{
              width: '100%',
              height: '60vh',
              border: 'none',
              borderRadius: '4px',
              backgroundColor: 'white'
            }}
            title="HTML Preview"
            sandbox="allow-scripts allow-forms allow-same-origin"
          />
        </Box>
      </Box>
    </Dialog>
  );
};

export default HtmlPreviewModal;